import { defineStore } from 'pinia';
import { api } from '@/api/client';
import type { AwardEarned, AwardsResponse } from '@/api/types';

interface AwardsState {
  /** Awards waiting for the overlay to play them in sequence. */
  queue: AwardEarned[];
  /** Currently-playing award (or null when nothing is on-screen). */
  current: AwardEarned | null;

  /** Last-fetched /api/awards payload — drives the Profile cards. */
  data: AwardsResponse | null;
}

export const useAwardsStore = defineStore('awards', {
  state: (): AwardsState => ({ queue: [], current: null, data: null }),
  actions: {
    /**
     * Push freshly-earned awards onto the queue. If the overlay is
     * idle, kick off playback. Safe to call with [] (no-op).
     */
    enqueue(awards: AwardEarned[] | undefined | null) {
      if (!awards || awards.length === 0) return;
      this.queue.push(...awards);
      if (!this.current) this.playNext();
    },
    playNext() {
      this.current = this.queue.shift() ?? null;
    },
    /** Called by the overlay when the user dismisses or auto-dismiss fires. */
    dismissCurrent() {
      this.current = null;
      // tiny inter-overlay beat so consecutive plays don't crossfade.
      window.setTimeout(() => { if (!this.current) this.playNext(); }, 200);
    },
    async refresh() {
      this.data = await api<AwardsResponse>('GET', '/api/awards');
    },
  },
});
